/*
 * AnythingElseCard — Slice 4 sharp-fill section-boundary catch-up.
 *
 * Fires once per high-yield L1 section (a / b / e) after the section's
 * required fields are resolved. Asks the user one open-ended question
 * ("Anything else about your stack I should know?") with a textarea
 * + skip. Submit pipes the prose through /api/intake/ingest source=
 * 'paste', which runs the extractor over the FULL registry. Any new
 * deltas come back as a BatchConfirmCard the parent renders next.
 *
 * Brand:
 *   - card surface #eef3ff, 1px brand ring
 *   - title in Plex Serif italic for section-divider feel
 *   - textarea in Plex Sans 13px
 *   - submit + skip in sentence case; no emoji
 *
 * The card is one-shot. Once submitted (or skipped), `submitted` flips
 * and rendering goes terminal — the same Turn entry stays in scroll
 * history but no longer accepts input.
 */

import React, { useState } from 'react';
import { Check, X } from 'lucide-react';

import type {
  AnythingElsePayload,
  BatchConfirmPayload,
  IngestResponse,
} from '../../lib/dossier-types';
import { ingest } from '../../lib/intakeApi';


export interface AnythingElseCardProps {
  payload: AnythingElsePayload;
  clientId?: string;
  /** Fires when the user sends prose. Parent uses the returned
   *  BatchConfirmPayload (when present) to push a BatchConfirmCard for
   *  the just-extracted deltas. */
  onSubmitted: (result: IngestResponse, batchConfirm: BatchConfirmPayload | null) => void;
  onSkipped: () => void;
  disabled?: boolean;
}


export const AnythingElseCard: React.FC<AnythingElseCardProps> = ({
  payload,
  clientId,
  onSubmitted,
  onSkipped,
  disabled,
}) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const canSubmit = text.trim().length >= 4 && !submitting && !done;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await ingest('paste', text.trim(), clientId);
      setDone(true);
      onSubmitted(res, res.batch_confirm ?? null);
    } catch (e) {
      console.error('[AnythingElseCard] submit failed', e);
      setError("Couldn't save that just now. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const skip = () => {
    if (submitting || done) return;
    setDone(true);
    onSkipped();
  };

  if (done) {
    return (
      <div className="aa-anything-else aa-anything-else--done">
        <p className="aa-anything-else-done-line">
          Got it. Moving on.
        </p>
      </div>
    );
  }

  return (
    <div className="aa-anything-else">
      <header className="aa-anything-else-header">
        <span className="aa-anything-else-eyebrow">{payload.section_label}</span>
        <h3 className="aa-anything-else-title">{payload.prompt}</h3>
      </header>

      <textarea
        className="aa-anything-else-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="A sentence or two is plenty. I'll pull anything useful into the dossier."
        disabled={submitting || disabled}
        rows={3}
        aria-label="Anything else"
      />

      {error && (
        <p className="aa-anything-else-error">{error}</p>
      )}

      <footer className="aa-anything-else-footer">
        <button
          type="button"
          className="aa-anything-else-primary"
          onClick={submit}
          disabled={!canSubmit || disabled}
        >
          <Check size={13} strokeWidth={2.5} />
          <span>Send</span>
        </button>
        <button
          type="button"
          className="aa-anything-else-secondary"
          onClick={skip}
          disabled={submitting || disabled}
        >
          <X size={12} strokeWidth={2.25} />
          <span>Skip</span>
        </button>
      </footer>
    </div>
  );
};

export default AnythingElseCard;
